import { Calendar, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const BOOKING_URL = "https://home.shortcutssoftware.com/ah#/_h/sites/6509/details";

const treatableAreas = [
  { area: 'Face & Upper Lip', description: 'Delicate areas treated with precision for smooth, hair-free skin.' },
  { area: 'Underarms', description: 'Say goodbye to daily shaving with long-lasting smoothness.' },
  { area: 'Arms & Hands', description: 'Achieve silky smooth arms without irritation.' },
  { area: 'Bikini & Brazilian', description: 'Comfortable, effective treatment for sensitive areas.' },
  { area: 'Legs (Full & Half)', description: 'Enjoy effortlessly smooth legs all year round.' },
  { area: 'Back & Chest', description: 'Large area treatments for a clean, hair-free look.' },
];

const howItWorks = [
  {
    step: '1',
    title: 'Consultation',
    description: 'We assess your skin and hair type to create a personalised treatment plan tailored to your goals.',
  },
  {
    step: '2',
    title: 'Treatment',
    description: 'Our advanced SHR system glides over the skin using a cooling tip, delivering gentle light energy that disables hair follicles without the sharp zap of traditional IPL.',
  },
  {
    step: '3',
    title: 'Results',
    description: 'Hair gradually reduces over a series of sessions. Most clients see significant reduction after 6-8 treatments, with maintenance sessions as needed.',
  },
];

export default function SuperHairRemoval() {
  return (
    <main className="min-h-screen bg-[#FFFDF9]" data-testid="shr-page">
      <SEO page="superHairRemoval" />

      {/* Hero + Treatment Areas + CTA (no benefits card) */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1709316054551-355656bd4815?w=1920&h=800&fit=crop"
            alt="Super Hair Removal treatment smooth skin Camberwell"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FFFDF9] via-[#FFFDF9]/95 to-[#FFFDF9]/70" />
        </div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#8D7B68]/10 border border-[#8D7B68]/30 rounded-full px-4 py-2 mb-6">
              <Zap className="w-4 h-4 text-[#8D7B68]" />
              <span className="text-sm text-[#5D4A3A] font-medium">Advanced Hair Removal</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-[#2C2420] leading-[1.1] mb-6">
              Super Hair Removal
            </h1>
            <p className="text-xl text-[#8D7B68] italic mb-4">
              "Painless, Effective, Long-Lasting"
            </p>
            <p className="text-lg text-[#5D4A3A] leading-relaxed mb-8">
              Tired of shaving, waxing, and dealing with ingrown hairs? Our Super Hair Removal (SHR) technology delivers smooth, hair-free skin with virtually no pain — even on sensitive areas. It's the smarter, gentler alternative to traditional laser and IPL.
            </p>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full px-8 py-6 text-lg" data-testid="shr-book-hero-btn">
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* What is SHR Banner */}
      <section className="py-16 bg-[#8D7B68]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 text-center text-white">
          <h2 className="text-2xl font-medium mb-3">What is Super Hair Removal?</h2>
          <p className="text-white/80 max-w-3xl mx-auto text-base md:text-lg">
            SHR is the next generation of permanent hair reduction. Unlike traditional IPL which delivers a single high-energy pulse, SHR uses a rapid succession of lower-energy pulses with an in-motion technique. This heats the hair follicle gradually and gently, making it effective on all skin types — including darker and tanned skin — with far less discomfort.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">The Process</p>
            <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
              How It Works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howItWorks.map((item) => (
              <div key={item.step} className="bg-white p-8 rounded-lg border border-[#D4C4B0]/50 text-center">
                <div className="w-12 h-12 rounded-full bg-[#8D7B68]/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-[#8D7B68] font-bold text-lg">{item.step}</span>
                </div>
                <h3 className="text-lg font-medium text-[#2C2420] mb-3">{item.title}</h3>
                <p className="text-sm text-[#5D4A3A]">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Treatment Areas */}
      <section className="py-24 md:py-32 bg-[#E8DED2]/30">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">Treatable Areas</p>
            <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
              Where We Can Treat
            </h2>
            <p className="text-lg text-[#5D4A3A] max-w-2xl mx-auto">
              SHR can safely and effectively treat virtually any area of the body where unwanted hair grows.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {treatableAreas.map((item) => (
              <div key={item.area} className="bg-white p-6 rounded-lg border border-[#D4C4B0]/50 hover:border-[#8D7B68]/50 transition-colors">
                <h3 className="text-lg font-medium text-[#2C2420] mb-2">{item.area}</h3>
                <p className="text-sm text-[#5D4A3A]">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Gallery */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">Smooth, Hair-Free Skin</p>
            <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
              The Results Speak for Themselves
            </h2>
            <p className="text-lg text-[#5D4A3A] max-w-2xl mx-auto">
              Permanently reduce unwanted hair with our advanced SHR technology. Suitable for all skin types, all year round.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="aspect-[3/4] rounded-lg overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1709316054551-355656bd4815?w=500&h=667&fit=crop"
                alt="Smooth skin after laser hair removal"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="aspect-[3/4] rounded-lg overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1708966847287-1eab5e87686b?w=500&h=667&fit=crop"
                alt="Hair-free smooth legs after SHR treatment"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="aspect-[3/4] rounded-lg overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1626900518828-cc4f78ecb72d?w=500&h=667&fit=crop"
                alt="Super hair removal smooth results"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#2C2420] text-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl md:text-4xl font-medium mb-6">
            Ready for Smooth, Hair-Free Skin?
          </h2>
          <p className="text-lg text-[#BCAAA4] max-w-2xl mx-auto mb-8">
            Book a free consultation and let us create a personalised hair removal plan for you. Say goodbye to shaving for good.
          </p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full px-10 py-6 text-lg" data-testid="shr-book-cta-btn">
              <Calendar className="w-5 h-5 mr-2" />
              Book Free Consultation
            </Button>
          </a>
        </div>
      </section>
    </main>
  );
}
